import { Button } from 'ui';
import { Dashboard, DashboardMainHeaderForm } from 'dashboard/components/dashboard';
import CooperativeMemberFormFields, {
  FormFields,
} from 'cooperatives/components/form/CooperativeMemberFormFields';
import { Page } from 'dashboard/components/dashboard/Breadcrumbs';

import { useForm, SubmitHandler, ErrorOption } from 'react-hook-form';
import { ApolloError, useLazyQuery, useMutation } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { setFormError } from 'utils/form';
import {
  CooperativeMemberCreated,
  CooperativeMemberCreateMutationVariables,
  COOPERATIVE_MEMBER_CREATE_MUTATION,
} from 'cooperatives/graphql/cooperativeMemberCreateMutation';
import {
  cooperativeMembersTypename,
  CooperativeMembersList,
  COOPERATIVE_MEMBERS_QUERY,
} from 'cooperatives/graphql/cooperativeMembersQuery';
import {
  CooperativesSelectList,
  COOPERATIVES_SELECT_QUERY,
  COOPERATIVES_SELECT_QUERY_VARIABLES,
} from 'cooperatives/graphql/cooperativesSelectQuery';
import useToastContext from 'ui/hooks/useToast';
import { useHistory } from 'react-router';
import { ToastProps } from 'ui/contexts/overlay/Toast';
import { useLoading } from 'ui/contexts/overlay/Loading';
import { useEffect } from 'react';
import { has } from 'lodash';

const DASHBOARD_COOPERATIVE_MEMBERS_ROUTE =
  '/dashboard/business-entities/cooperative-members';

const BREADCRUMB_PAGES: Page[] = [
  { name: 'Cooperados', route: DASHBOARD_COOPERATIVE_MEMBERS_ROUTE, current: false },
  {
    name: 'Novo Cooperado',
    route: null,
    current: true,
  },
];

const TITLE = 'Novo Cooperado';
const CREATE_ERROR_TOAST: ToastProps = {
  text: 'Houve um erro ao tentar criar o cooperado',
  title: 'Algo deu errado!',
  variant: 'danger',
};

const CREATE_SUCCESS_TOAST: ToastProps = {
  title: 'Sucesso!',
  variant: 'primary',
  text: 'Sucesso ao cadastrar o Cooperado.',
};

export default function CreateCooperativeMembersPage() {
  const { push } = useHistory();
  const { addToast } = useToastContext();
  const { t } = useTranslation();
  const {
    control,
    register,
    setError,
    clearErrors,
    handleSubmit,
    formState: { errors },
  } = useForm<FormFields>();
  const { showLoading, closeLoading, LoadingOverlay } = useLoading();

  const [cooperativeMemberCreateMutation, { loading: loadingCreate }] = useMutation<
    CooperativeMemberCreated,
    CooperativeMemberCreateMutationVariables
  >(COOPERATIVE_MEMBER_CREATE_MUTATION, {
    onError(error: ApolloError) {
      if (has(error.graphQLErrors[0], 'details')) {
        addToast(CREATE_ERROR_TOAST);
      }

      setFormError(
        error,
        (field: string, error: ErrorOption) => {
          setError(field as keyof FormFields, error);

          setTimeout(() => clearErrors(), 2500);
        },
        t
      );
    },
    onCompleted() {
      addToast(CREATE_SUCCESS_TOAST);
      push(DASHBOARD_COOPERATIVE_MEMBERS_ROUTE);
    },
  });

  const [
    cooperatives,
    { data: cooperative, loading: cooperativeLoading, refetch: cooperativeRefetch },
  ] = useLazyQuery<CooperativesSelectList>(
    COOPERATIVES_SELECT_QUERY,
    COOPERATIVES_SELECT_QUERY_VARIABLES
  );

  const onSubmit: SubmitHandler<FormFields> = (cooperativeMemberCreateInput) =>
    cooperativeMemberCreateMutation({
      variables: {
        cooperativeMemberCreateInput: {
          ...cooperativeMemberCreateInput,
          cooperativeMemberOptionalEmail:
            cooperativeMemberCreateInput.cooperativeMemberOptionalEmail?.split(', '),
        },
      },
      update(cache, { data }) {
        const existingData = cache.readQuery<CooperativeMembersList>({
          query: COOPERATIVE_MEMBERS_QUERY,
        });
        cache.writeQuery({
          query: COOPERATIVE_MEMBERS_QUERY,
          data: {
            cooperativeMembers: {
              __typename: cooperativeMembersTypename,
              afterCursor: existingData?.cooperativeMembers.afterCursor || null,
              beforeCursor: existingData?.cooperativeMembers.beforeCursor || null,
              entries: [
                data?.cooperativeMemberCreate,
                ...(existingData?.cooperativeMembers.entries || []),
              ],
            },
          },
        });
      },
    });

  const isLoading = cooperativeLoading || loadingCreate;

  useEffect(() => {
    if (isLoading) {
      showLoading();
      return;
    }

    closeLoading();
  }, [isLoading, showLoading, closeLoading]);

  useEffect(() => {
    cooperativeRefetch && cooperativeRefetch();
    cooperatives();
  }, [cooperatives, cooperativeRefetch]);

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <LoadingOverlay />
      <Dashboard
        dashboardHeader={<></>}
        dashboardMainHeaderTitle={
          <DashboardMainHeaderForm breadcrumbPages={BREADCRUMB_PAGES} title={TITLE}>
            <Button type="submit" size="sm" disabled={isLoading}>
              Salvar
            </Button>
          </DashboardMainHeaderForm>
        }
      >
        <CooperativeMemberFormFields
          formErrors={errors}
          control={control}
          register={register}
          cooperatives={cooperative?.cooperatives.entries}
        />
      </Dashboard>
    </form>
  );
}
